<template>
  <nav>
    <v-row class="fill-height" align="center">
      <v-col cols="12" sm="10" md="8" offset-sm="1" offset-md="2" align="center">
        <v-form ref="form" v-model="valid" @keyup.enter="createHub" @submit.prevent>
          <AlertMessages v-if="alertValue" alert-type="error" :alert-msg="alertMsg" @reset="alertValue = false" />
          <v-card max-width="800" elevation="0" rounded="xxl" color="background">
            <v-card-title class="text-h5 font-weight-bold text-center no-break justify-center mb-4">
              {{ $t('createHubTitle') }}
            </v-card-title>
            <v-card-subtitle class=" text-center justify-center mb-4 no-break">
              {{ $t('createHubSubTitle') }}
            </v-card-subtitle>
            <v-card-text>
              <v-text-field v-model="title" data-testid="title-input" solo hide-details="auto"
                class="rounded-xxl text-body-2 mb-3" :placeholder="$t('formTitlePlaceholder')" :rules="titleRules" />
              <v-textarea v-model="descriptionHub" solo hide-details="auto" class="rounded-xxl text-body-2"
                :placeholder="$t('labelDescriptionHub')" :rules="descriptionRules" />
            </v-card-text>
          </v-card>
          <v-row>
            <v-col cols="10" md="6" sm="10" offset-md="3" offset="1" align="center">
              <v-hover>
                <template v-slot:default="{ hover }">
                  <v-card data-testid="hub-cover-card" rounded="md" class="mx-auto pa-0 rounded-xxl"
                    :elevation="hover ? 8 : 5" max-width="600">
                    <v-card-actions class="pa-0 ">
                      <v-img class="rounded-xxl" max-height="280" :src="coverURL" />
                      <v-file-input ref="file_input_cover" v-model="coverImageData" accept="image/png, image/jpeg"
                        data-testid="cover-file-input" class="d-none" @change="uploadCover" />
                      <div class="center-button-on-image">
                        <v-btn :loading="loadingCover" data-testid="upload-cover-button" mx-auto color="accent"
                          class="rounded-xxl text-none px-10" elevation="2" relative bottom left @click="showUploadCover">
                          {{ $t('upload') }}
                        </v-btn>
                        <v-btn data-testid="upload-unsplash-cover-button" mx-auto color="accent"
                          class="rounded-xxl text-none px-10 d-block mx-auto mt-3" :class="{'disable-events': loadingCover}" elevation="2" relative bottom left @click="toggleUnsplash">
                          {{ $t('browse') }}
                        </v-btn>
                      </div>
                    </v-card-actions>
                  </v-card>
                </template>
              </v-hover>
            </v-col>
          </v-row>
          <v-row>
            <v-col align="center">
              <FormFooter next-button-text="create" :loading="loading" @proceedSubmit="createHub" />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    <UnsplashSearch 
      v-if="showUnsplash"
      ref="unsplashDialog"
      :visible="showUnsplash"
      data-testid="unsplash-dialog"
      @close="showUnsplash = false"
      @applyPhoto="embedUnsplash"
    />
  </nav>
</template>
<script>
import { ruleRequired, ruleValidMin2Max70Characters, ruleValidMin2Characters } from '../../rules/formRules.js';
import i18n from '../../plugins/i18n'
import { createHub, uploadCoverImage } from '../../api/hubApi'
import FormFooter from '../../components/FormFooter'
import AlertMessages from '../../components/AlertMessages'
import UnsplashSearch from '@/components/unsplash/UnsplashSearch.vue';

export default {
  components: {
    FormFooter, 
    AlertMessages,
    UnsplashSearch
  },
  data() {
    return {
      valid: true,
      title: '',
      titleRules: ruleRequired('labelTitle').concat(ruleValidMin2Max70Characters('labelTitle')),
      descriptionHub: '',
      descriptionRules: ruleValidMin2Characters('labelDescription'),
      alertMsg: '',
      buttonDisabled: false,
      coverImageData: null,
      coverURL: require('../../assets/background-hub.jpg'),
      coverPath: null,
      loadingCover: false,
      alertValue: false,
      spaceId: window.$cookies.get('space_id'),
      showUnsplash: false
    }
  },
  computed: {
    loading() {
      if (this.loadingCover || this.buttonDisabled) {
        return true
      }
      return false
    }
  },
  methods: {
    async createHub() {
      this.$refs.form.validate()
      if (this.valid) {
        this.buttonDisabled = true
        try {
          let data = {
            data: {
              type: "hubs",
              attributes: {
                title: this.title.trim(),
                image: this.coverPath,
                description: this.descriptionHub.trim()
              },
              relationships: {
                space: {
                  data: {
                    type: "spaces",
                    id: "" + this.spaceId + ""
                  }
                }
              }
            }
          };
          var response = await createHub(data);
          if (response) {
            if (response.status == 201) {
              this.$router.push({ name: 'ViewHub', params: { id: response.data.data.id, successfullyCreatedHub: true } })
            }
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true
            if (e.response.status == 403) {
              this.$router.push({ name: 'PageAccessDenied' })
            } else {
              this.alertValue = true
              this.alertMsg = i18n.t('serverError')
            }
          } else {
            this.alertMsg = i18n.t('serverError')
          }
        }
        this.buttonDisabled = false
      }
    },
    async uploadCover() {
      if (this.coverImageData.size > 5 * 1024 * 1024) {
        this.alertMsg = i18n.t('uploadCoverError')
        this.alertValue = true
      } else {
        this.loadingCover = true
        try {
          let data = new FormData();
          data.append('file', this.coverImageData);
          data.append('type', 'cover');
          data.append('space_id', this.spaceId);

          const response = await uploadCoverImage(data)
          if (response.status == 200) {
            this.loadingCover = false
            this.alertMsg = ''
            this.coverURL = URL.createObjectURL(this.coverImageData)
            this.coverPath = response.data.meta.detail
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true
            if (e.response.status == 422) {
              for (let i = 0; i < e.response.data.errors.length; i++) {
                if (e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize') {
                  this.alertMsg = i18n.t('uploadCoverError')
                } else {
                  this.alertMsg = i18n.t('serverError')
                }
              }
            } else if (e.response.status == 403) {
              for (let i = 0; i < e.response.data.errors.length; i++) {
                if (e.response.data.errors[i].detail == 'Storage is full') {
                  this.alertMsg = i18n.t('storageFullError')
                }
                else {
                  this.$router.push({ name: 'PageAccessDenied' })
                }
              }
            } else {
              this.alertMsg = i18n.t('serverError')
            }
          } else {
            this.alertValue = true
            this.alertMsg = i18n.t('serverError')
          }
          this.loadingCover = false
        }
      }
    },

    showUploadCover() {
      this.$refs.file_input_cover.$refs.input.click()
    },

    toggleUnsplash(){
      this.showUnsplash = !this.showUnsplash
    },

    embedUnsplash(photo){
      this.showUnsplash = false
      this.coverURL = photo.urls.regular
      this.coverPath = photo.urls.regular
    }
  }
}
</script>
